<script lang="ts">
  interface Props {
    label: string
    name: string
    value: string
    type?: 'text' | 'email' | 'password' | 'date' | 'number'
    required?: boolean
    error?: string
    placeholder?: string
  }

  let {
    label,
    name,
    value = $bindable(''),
    type = 'text',
    required = false,
    error,
    placeholder,
  }: Props = $props()

  // Basic email shape check - matches the wizard's needs without pretending to
  // be RFC 5322. The server is the authority; this is just a helpful hint.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  // Self-validation shows only after the field is "touched" (blurred once), so
  // we don't scold users mid-typing. An explicit `error` prop always wins.
  let touched = $state(false)
  const selfError = $derived.by(() => {
    if (!touched) return ''
    if (required && value.trim() === '') return 'Required'
    if (type === 'email' && value.trim() !== '' && !EMAIL_RE.test(value.trim())) {
      return 'Enter a valid email address'
    }
    return ''
  })
  const shownError = $derived(error || selfError)
  const errorId = $derived(`${name}-error`)
</script>

<div class="form-field">
  <label for={name}>{label}{required ? ' *' : ''}</label>
  <input
    id={name}
    {name}
    {type}
    {placeholder}
    {required}
    bind:value
    class:has-error={shownError}
    aria-invalid={shownError ? 'true' : undefined}
    aria-describedby={shownError ? errorId : undefined}
    onblur={() => (touched = true)}
  />
  {#if shownError}
    <span class="field-error" id={errorId}>{shownError}</span>
  {/if}
</div>

<style>
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
  }
  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  input {
    padding: 10px 12px;
    min-height: 44px; /* comfortable touch target */
    box-sizing: border-box;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 1rem; /* 16px avoids iOS zoom-on-focus */
  }
  input:focus {
    outline: 2px solid var(--brand);
    outline-offset: -1px;
  }
  input.has-error {
    border-color: var(--err-ink);
  }
  .field-error {
    color: var(--err-ink);
    font-size: 0.75rem;
  }
</style>
